import { useEffect, useRef } from 'react';
import { Gift, Smartphone, ArrowDownToLine } from 'lucide-react';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from './ui/drawer';
import { Button } from './ui/button';
import { getStoreTarget, APP_DOWNLOAD_URL } from '../utils/appLinks';
import { trackEvent } from '../utils/analytics';

/**
 * "Get the app to claim" sheet (Business Rule #5 amendment — app-download
 * claim gate). Shown on WEB when a customer taps a free-coffee claim
 * affordance while `claim_requires_app` is set on their `/free-coffee/
 * progress` payload — the earned reward is theirs, but claiming needs the
 * Penguin Bean app on their phone at least once. Never renders inside the
 * native app (the flag is false there).
 *
 * Mobile web gets a DIRECT store link (`getStoreTarget()` — App Store /
 * Google Play), matching the existing AppDownloadBanner and
 * AppInstallNudgeSheet; a desktop browser falls back to the
 * one-link-for-both-stores install page (`APP_DOWNLOAD_URL`), which is the
 * only surface where the platform is unknown. Fires
 * `free_coffee_app_gate_shown` once per open for gate→install conversion
 * measurement.
 *
 * Controlled: parent owns `open` and flips it via `onOpenChange`.
 */
export default function AppRequiredSheet({ open, onOpenChange }) {
  const target = getStoreTarget();
  const trackedRef = useRef(false);

  useEffect(() => {
    if (open && !trackedRef.current) {
      trackedRef.current = true;
      trackEvent('free_coffee_app_gate_shown', {
        metadata: { platform: target?.platform || 'desktop' },
      });
    }
    if (!open) trackedRef.current = false;
  }, [open, target]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="px-6 pb-8" data-testid="app-required-sheet">
        {/* App icon with download badge — same treatment as the install nudge */}
        <div className="flex justify-center mt-2 mb-4">
          <div className="relative w-[76px] h-[76px] rounded-[22px] bg-muted border border-border flex items-center justify-center">
            <img src="/logo512.png" alt="Penguin Bean Coffee app" className="w-14 h-14 rounded-[14px]" />
            <div className="absolute -right-1.5 -top-1.5 w-[26px] h-[26px] rounded-full bg-[hsl(var(--accent))] text-white border-2 border-background flex items-center justify-center">
              <ArrowDownToLine className="w-3.5 h-3.5" strokeWidth={2.5} />
            </div>
          </div>
        </div>

        <DrawerTitle className="font-fraunces text-[22px] font-bold tracking-tight text-center leading-tight mb-2">
          Your free coffee is ready!
        </DrawerTitle>
        <DrawerDescription className="text-center text-muted-foreground text-[14.5px] leading-relaxed mb-4">
          Download the Penguin Bean app to claim it — your progress and reward
          are already saved to your account.
        </DrawerDescription>

        <div
          className="flex items-center justify-center gap-2.5 rounded-lg px-4 py-3.5 mb-5 text-sm font-bold bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30"
          data-testid="app-required-reward-note"
        >
          <Gift className="w-[17px] h-[17px] text-[hsl(var(--accent))] shrink-0" strokeWidth={2} />
          <span className="text-[hsl(var(--coffee))]">One free coffee, waiting for you in the app</span>
        </div>

        {/* Direct store link on phones (owner decision 2026-09-01, matching
            AppDownloadBanner / AppInstallNudgeSheet — when the platform is
            known, the direct link skips the smart page's redirect hop). The
            /app one-link page is the fallback only where the platform is
            unknown (desktop). */}
        <Button asChild className="w-full rounded-lg py-6 text-[15.5px] font-bold gap-2" data-testid="app-required-cta">
          <a href={target ? target.url : APP_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
            <Smartphone className="w-[17px] h-[17px]" />
            {target
              ? (target.platform === 'ios' ? 'Get it on the App Store' : 'Get it on Google Play')
              : 'Download the app'}
          </a>
        </Button>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="w-full mt-1.5 py-3 text-[13.5px] font-semibold text-muted-foreground"
          data-testid="app-required-dismiss"
        >
          Maybe later
        </button>
      </DrawerContent>
    </Drawer>
  );
}
